import { useEffect, useRef, useState } from "react";
import SEOHead from "@/components/SEOHead";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { Phone, Mail, MapPin, Clock, MessageSquare, CircleCheck, Plus, Minus, ArrowRight } from "lucide-react";
import { BUSINESS_INFO } from "@/data/businessInfo";
import QuoteForm, { OFFICE_CONSULTATION_SERVICE } from "@/components/home/QuoteForm";
import { useSearchParams } from "react-router-dom";
import { CONTACT_SMS } from "@/data/serviceSms";
import { trackConversion } from "@/lib/track";

const SITE = "https://alltimepaintandplaster.com";
const CANONICAL = `${SITE}/contact/`;
const DIRECTIONS_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  `${BUSINESS_INFO.name}, ${BUSINESS_INFO.addressText}`,
)}`;
const MAP_EMBED_URL = "https://storage.googleapis.com/maps-solutions-ogyde1zndf/locator-plus/r47y/locator-plus.html";

const FAQS = [
  {
    question: "Do you offer free estimates?",
    answer: "Yes. Every estimate is free and comes with no obligation. We'll visit your space, review the project, and provide a clear, written quote.",
  },
  {
    question: "What areas do you serve?",
    answer: `We serve ${BUSINESS_INFO.serviceAreasText}. Our office is in Wayne, NJ, and we travel throughout these counties for in-person consultations and projects.`,
  },
  {
    question: "Do you do both plaster and painting projects?",
    answer: "Yes. We specialize in natural plaster finishes and also provide full interior and exterior painting services. Many projects combine both for a complete, finished look.",
  },
  {
    question: "How soon can you start?",
    answer: "It depends on the season and project size. After your estimate is approved, we'll schedule work at a time that fits your timeline and keep you updated throughout.",
  },
  {
    question: "Can I text photos of my project?",
    answer: "Yes. Text your project photos, town and a brief description to (201) 701-8248. Photos help us understand the scope and prepare for your on-site estimate.",
  },
  {
    question: "Can I visit your Wayne office to review samples?",
    answer: "Yes. Office consultations are available by appointment. You can review limewash colors, decorative plaster samples, microcement finishes and other specialty-finish options with our team.",
  },
  {
    question: "How quickly will I receive my estimate?",
    answer: "After your consultation, we’ll confirm when to expect your written proposal. Timing depends on the size and complexity of your project.",
  },
];

const VALUE_POINTS = [
  "Personal, knowledgeable estimate—not a high-pressure sales visit",
  "A written proposal based on your agreed project scope",
  "No pressure and no hidden fees",
];

const TRUST_ROW = ["NJ Licensed & Insured", "Free Written Estimates", "Owner-Managed Projects", "Serving North Jersey"];

const CONTACT_PAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "@id": `${CANONICAL}#webpage`,
  url: CANONICAL,
  name: "Contact All Time Paint & Plaster Co.",
  isPartOf: { "@id": `${SITE}/#website` },
  about: { "@id": `${SITE}/#organization` },
  mainEntity: { "@id": `${SITE}/#organization` },
};

const btnBase =
  "inline-flex items-center justify-center gap-2 min-h-[48px] px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2";

const scrollToForm = (e: React.MouseEvent) => {
  e.preventDefault();
  document.getElementById("estimate-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
};

/** Google Maps embed that only loads once it nears the viewport. */
const LazyMap = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [load, setLoad] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
      setLoad(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setLoad(true);
          io.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className="h-[520px] md:h-[560px] w-full border border-border bg-muted overflow-hidden">
      {load ? (
        <iframe
          title={`${BUSINESS_INFO.name} office location — ${BUSINESS_INFO.addressText}`}
          src={MAP_EMBED_URL}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      ) : (
        <div className="h-full w-full flex items-center justify-center text-sm text-muted-foreground gap-2">
          <MapPin className="w-4 h-4 text-clay" aria-hidden="true" /> {BUSINESS_INFO.addressText}
        </div>
      )}
    </div>
  );
};

const Contact = () => {
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source") || "/contact";
  const project = searchParams.get("project") || undefined;
  const officeConsult = searchParams.get("consultation") === "office";

  useEffect(() => {
    if (officeConsult) {
      const t = setTimeout(
        () => document.getElementById("estimate-form")?.scrollIntoView({ behavior: "smooth", block: "start" }),
        300,
      );
      return () => clearTimeout(t);
    }
  }, [officeConsult]);

  const textClick = (location: string) => () => trackConversion("text_click", { location });

  return (
    <main className="min-h-screen bg-background pb-20 md:pb-0">
      <SEOHead
        title="Contact All Time Paint & Plaster Co. | Wayne, NJ"
        description="Contact All Time Paint & Plaster Co. for a free painting, plaster, limewash, wallpaper or specialty-finish estimate in Wayne and throughout North Jersey."
        canonicalUrl={CANONICAL}
        faqItems={FAQS}
        extraSchemas={[CONTACT_PAGE_SCHEMA]}
      />

      <Header />

      {/* 1. Hero */}
      <section className="bg-secondary text-secondary-foreground">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-11 md:py-16 text-center">
          <p className="text-[11px] uppercase tracking-[0.28em] text-secondary-foreground/60 mb-4">Free Estimates</p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-5 leading-[1.05]">Request Your Free Estimate</h1>
          <p className="text-secondary-foreground/80 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Tell us about your project and we’ll follow up to schedule your consultation. No obligation, no pressure and no hidden fees.
          </p>
          <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 justify-center">
            <a
              href="#estimate-form"
              onClick={scrollToForm}
              className={`${btnBase} w-full sm:w-auto bg-clay-light hover:bg-clay text-secondary hover:text-secondary-foreground focus-visible:ring-clay-light`}
            >
              Request a Free Estimate
            </a>
            <a
              href={CONTACT_SMS}
              onClick={textClick("contact_hero")}
              className={`${btnBase} w-full sm:w-auto border border-secondary-foreground/40 hover:border-secondary-foreground text-secondary-foreground focus-visible:ring-secondary-foreground`}
            >
              <MessageSquare className="w-4 h-4" aria-hidden="true" />
              Text Project Photos
            </a>
          </div>
        </div>
      </section>

      {/* 2–3. Value statements + trust row */}
      <section className="bg-background border-b border-border">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6 md:py-8">
          <ul className="grid gap-3 md:grid-cols-3 md:gap-6">
            {VALUE_POINTS.map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-foreground">
                <CircleCheck className="w-5 h-5 shrink-0 text-foreground" strokeWidth={1.25} aria-hidden="true" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 pt-4 border-t border-border text-center text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-muted-foreground leading-relaxed flex flex-wrap justify-center gap-y-2">
            {TRUST_ROW.map((t, i) => (
              <span key={t} className="whitespace-nowrap">
                {t}
                {i < TRUST_ROW.length - 1 && <span className="mx-2 text-clay" aria-hidden="true">•</span>}
              </span>
            ))}
          </p>
        </div>
      </section>

      {/* 4. Form + direct contact */}
      <section className="bg-muted/40 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12 md:py-20 grid gap-10 lg:gap-12 lg:grid-cols-[1.2fr_1fr] items-start">
          <div id="estimate-form" className="scroll-mt-24">
            <p className="text-[10px] uppercase tracking-[0.28em] text-clay mb-3">Tell Us About Your Project</p>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Complete the short form below, or{" "}
              <a href={CONTACT_SMS} onClick={textClick("form_intro")} className="text-foreground underline underline-offset-4 hover:text-clay">
                text us photos
              </a>{" "}
              for a faster initial project review.
            </p>
            <QuoteForm
              key={officeConsult ? "office" : searchParams.get("service") || "default"}
              variant="contact"
              sourcePage={officeConsult ? "/contact/?consultation=office" : source}
              defaultProjectType={project}
              defaultService={officeConsult ? OFFICE_CONSULTATION_SERVICE : undefined}
            />
          </div>

          <div className="space-y-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-clay mb-3">Contact Info</p>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground mb-4 leading-tight">Let's talk about your project</h2>
              <p className="text-muted-foreground leading-relaxed">
                Prefer to reach us directly? We respond to every message as quickly as possible.
              </p>
            </div>

            {/* Text — primary */}
            <div className="p-6 border border-clay/40 bg-card">
              <div className="flex items-start gap-4">
                <MessageSquare className="w-5 h-5 text-clay mt-1 shrink-0" aria-hidden="true" />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-1">Text Project Photos</p>
                  <p className="text-foreground font-medium text-xl">{BUSINESS_INFO.phone}</p>
                  <p className="text-sm text-muted-foreground mt-1">The fastest way to share your project details and photos.</p>
                </div>
              </div>
              <a
                href={CONTACT_SMS}
                onClick={textClick("contact_card")}
                className={`${btnBase} mt-5 w-full bg-clay hover:bg-clay-dark text-primary-foreground focus-visible:ring-clay`}
              >
                <MessageSquare className="w-4 h-4" aria-hidden="true" />
                Start a Text
              </a>
            </div>

            <div className="grid gap-3">
              <a
                href={BUSINESS_INFO.phoneLink}
                onClick={() => trackConversion("phone_click", { location: "contact_card" })}
                className="flex items-start gap-4 p-5 border border-border bg-card hover:border-clay/40 transition-colors"
              >
                <Phone className="w-5 h-5 text-clay mt-1 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-1">Call Our Office</p>
                  <p className="text-foreground font-medium text-lg">{BUSINESS_INFO.phone}</p>
                </div>
              </a>

              <a
                href={BUSINESS_INFO.emailLink}
                onClick={() => trackConversion("email_click", { location: "contact_card" })}
                className="flex items-start gap-4 p-5 border border-border bg-card hover:border-clay/40 transition-colors"
              >
                <Mail className="w-5 h-5 text-clay mt-1 shrink-0" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-1">Email</p>
                  <p className="text-foreground font-medium break-words">{BUSINESS_INFO.email}</p>
                </div>
              </a>

              <div className="flex items-start gap-4 p-5 border border-border bg-card">
                <Clock className="w-5 h-5 text-clay mt-1 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-1">Hours</p>
                  <p className="text-foreground font-medium">Monday – Sunday: {BUSINESS_INFO.hours.weekdays}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Office & sample consultations */}
      <section className="bg-background border-b border-border">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-12 md:py-20 grid gap-8 md:grid-cols-[1.3fr_1fr] items-center">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-clay mb-3">Office & Sample Consultations</p>
            <h2 className="font-heading text-3xl md:text-4xl text-foreground mb-5 leading-tight">See finishes in person in Wayne</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Visit our Wayne office to review limewash colors, decorative plaster samples, microcement finishes and other specialty-finish options in person.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Office visits are available by appointment so we can give your project our full attention.
            </p>
          </div>
          <div className="border border-border bg-card p-6 md:p-8">
            <MapPin className="w-5 h-5 text-clay mb-3" aria-hidden="true" />
            <address className="not-italic text-foreground font-medium leading-relaxed">
              1581 Route 23 South, Suite 1
              <br />
              Wayne, NJ 07470
            </address>
            <div className="mt-6 flex flex-col gap-3">
              <a
                href="/contact/?consultation=office#estimate-form"
                onClick={(e) => {
                  trackConversion("office_consultation_click", { location: "office_section" });
                  if (!officeConsult) return; // full navigation preselects the service
                  scrollToForm(e);
                }}
                className={`${btnBase} w-full bg-clay hover:bg-clay-dark text-primary-foreground focus-visible:ring-clay`}
              >
                Book an Office Consultation
              </a>
              <a
                href={DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackConversion("directions_click", { location: "office_section" })}
                className="inline-flex items-center justify-center gap-2 min-h-[48px] text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground hover:text-clay transition-colors"
              >
                Get Directions <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Service area */}
      <section className="bg-muted/40 border-b border-border">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-10 md:py-14">
          <p className="text-[10px] uppercase tracking-[0.28em] text-clay mb-4">Service Area</p>
          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-2 text-sm text-foreground">
            {BUSINESS_INFO.serviceAreas.map((area) => (
              <li key={area} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-clay rounded-full" aria-hidden="true" />
                {area}, NJ
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 7. Map */}
      <section className="bg-background">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-10 md:py-14">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-clay mb-2">Find Us</p>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground leading-tight">Wayne, NJ</h2>
            </div>
            <a
              href={DIRECTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackConversion("directions_click", { location: "map_section" })}
              className="inline-flex items-center gap-2 min-h-[48px] text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground hover:text-clay transition-colors"
            >
              Get Directions <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </a>
          </div>
          <LazyMap />
        </div>
      </section>

      {/* 8. FAQ */}
      <section className="bg-muted/40 border-t border-border">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-12 md:py-16">
          <p className="text-[10px] uppercase tracking-[0.28em] text-clay mb-3 text-center">FAQ</p>
          <h2 className="font-heading text-3xl md:text-4xl text-foreground mb-8 text-center leading-tight">Common Questions</h2>
          <div className="border-t border-border">
            {FAQS.map((faq) => (
              <details key={faq.question} className="group border-b border-border">
                <summary className="flex items-center justify-between gap-4 py-4 cursor-pointer list-none [&::-webkit-details-marker]:hidden min-h-[48px]">
                  <h3 className="font-heading text-base md:text-lg text-foreground">{faq.question}</h3>
                  <Plus className="w-4 h-4 shrink-0 text-foreground group-open:hidden" aria-hidden="true" />
                  <Minus className="w-4 h-4 shrink-0 text-foreground hidden group-open:block" aria-hidden="true" />
                </summary>
                <p className="pb-5 pr-8 text-muted-foreground leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* 9. Final CTA */}
      <section className="bg-secondary text-secondary-foreground">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-12 md:py-16 text-center">
          <h2 className="font-heading text-3xl md:text-4xl leading-tight mb-4">Ready to Discuss Your Project?</h2>
          <p className="text-secondary-foreground/80 leading-relaxed max-w-2xl mx-auto">
            Whether you’re planning a complete repaint or exploring a custom specialty finish, tell us what you have in mind.
          </p>
          <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 justify-center">
            <a
              href="#estimate-form"
              onClick={scrollToForm}
              className={`${btnBase} w-full sm:w-auto bg-clay-light hover:bg-clay text-secondary hover:text-secondary-foreground focus-visible:ring-clay-light`}
            >
              Request a Free Estimate
            </a>
            <a
              href={CONTACT_SMS}
              onClick={textClick("contact_final_cta")}
              className={`${btnBase} w-full sm:w-auto border border-secondary-foreground/40 hover:border-secondary-foreground text-secondary-foreground focus-visible:ring-secondary-foreground`}
            >
              <MessageSquare className="w-4 h-4" aria-hidden="true" />
              Text Project Photos
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Contact;
